import React, { createContext, useContext, useId, useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './collapsible';
import { cn } from 'src/lib/utils';

/**
 * Minimal single-open Accordion built on top of the existing Collapsible primitive.
 * Supports controlled (`value` + `onValueChange`) and uncontrolled (`defaultValue`) usage.
 * Keyboard: Radix's CollapsibleTrigger already handles Space/Enter on the button.
 */

const AccordionContext = createContext({
  openValue: null,
  setOpenValue: () => {},
  collapsible: true,
});

const ItemContext = createContext({ value: null, triggerId: null, contentId: null });

function Accordion({
  type = 'single',
  value,
  defaultValue = null,
  onValueChange,
  collapsible = true,
  className,
  children,
  ...props
}) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = value !== undefined;
  const openValue = isControlled ? value : internalValue;

  const setOpenValue = useCallback(
    (next) => {
      if (!isControlled) setInternalValue(next);
      if (onValueChange) onValueChange(next);
    },
    [isControlled, onValueChange],
  );

  if (type !== 'single') {
    console.warn('[Accordion] only type="single" is implemented');
  }

  return (
    <AccordionContext.Provider value={{ openValue, setOpenValue, collapsible }}>
      <div className={cn('divide-y divide-border rounded-lg border border-border', className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

const AccordionItem = React.forwardRef(function AccordionItem(
  { value, className, children, ...props },
  ref,
) {
  const { openValue, setOpenValue, collapsible } = useContext(AccordionContext);
  const triggerId = useId();
  const contentId = useId();
  const isOpen = openValue === value;

  const handleOpenChange = (next) => {
    if (next) {
      setOpenValue(value);
    } else if (collapsible) {
      setOpenValue(null);
    }
  };

  return (
    <ItemContext.Provider value={{ value, triggerId, contentId }}>
      <Collapsible
        ref={ref}
        open={isOpen}
        onOpenChange={handleOpenChange}
        className={cn('first:rounded-t-lg last:rounded-b-lg', className)}
        {...props}>
        {children}
      </Collapsible>
    </ItemContext.Provider>
  );
});

const AccordionTrigger = React.forwardRef(function AccordionTrigger(
  { className, children, actions, ...props },
  ref,
) {
  const { openValue } = useContext(AccordionContext);
  const { value, triggerId, contentId } = useContext(ItemContext);
  const isOpen = openValue === value;

  return (
    <div className='flex w-full items-center gap-1 pr-3 transition-colors hover:bg-muted/40'>
      <CollapsibleTrigger asChild>
        <button
          ref={ref}
          type='button'
          id={triggerId}
          aria-controls={contentId}
          aria-expanded={isOpen}
          data-state={isOpen ? 'open' : 'closed'}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 pr-1 text-left text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
            className,
          )}
          {...props}>
          <span className='flex-1 min-w-0'>{children}</span>
        </button>
      </CollapsibleTrigger>
      {actions ? <div className='flex shrink-0 items-center'>{actions}</div> : null}
      <CollapsibleTrigger asChild>
        <button
          type='button'
          tabIndex={-1}
          aria-hidden='true'
          data-state={isOpen ? 'open' : 'closed'}
          className='flex shrink-0 items-center justify-center py-3 focus:outline-none'>
          <ChevronDown
            size={18}
            strokeWidth={1.75}
            className={cn('shrink-0 text-muted-foreground transition-transform duration-200', isOpen && 'rotate-180')}
          />
        </button>
      </CollapsibleTrigger>
    </div>
  );
});

const AccordionContent = React.forwardRef(function AccordionContent(
  { className, children, ...props },
  ref,
) {
  const { triggerId, contentId } = useContext(ItemContext);

  return (
    <CollapsibleContent
      ref={ref}
      id={contentId}
      role='region'
      aria-labelledby={triggerId}
      className={cn('overflow-hidden', className)}
      {...props}>
      <div className='px-4 pb-4 pt-1'>{children}</div>
    </CollapsibleContent>
  );
});

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
